/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { AfterViewInit, ElementRef, HostListener, Injectable, OnDestroy, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Type } from '@gh/core-type';
import { KEY_END, KEY_HOME, KEY_LEFT, KEY_RIGHT } from '@gh/core-ui';
import { ICellEditorParams } from 'ag-grid-community';
import { defer, includes, isFunction, isNil } from 'lodash';
import { EditableCellModel } from '../../../state/grid-state.interfaces';
import { createCellFormControl } from '../../grid-cell.utils';
import { GridCellEditorComponent } from '../../grid-component.interfaces';

const STOPPED_KEYS = [KEY_LEFT, KEY_RIGHT, KEY_HOME, KEY_END];

export class AbstractGridInputCellEditorComponent implements GridCellEditorComponent, AfterViewInit, OnDestroy {
  required: boolean = false;

  control: FormControl;
  editorOptions?: any;

  @ViewChild('input', { static: false }) inputEl: ElementRef;

  type?: Type<any>;

  private isSelectOnFocus = true;
  private stopEditing: () => void;
  private focusTimeout: number;

  get maxLength(): number {
    const { type } = this;
    return type && type['maxLength'] || -1;
  }

  // tslint:disable-next-line:contextual-lifecycle
  ngAfterViewInit(): void {
    this.focusTimeout = defer(() => {
      this.inputEl.nativeElement.focus();
      if (this.isSelectOnFocus) {
        this.inputEl.nativeElement.select();
      }
      this.focusTimeout = 0;
    });
  }

  ngOnDestroy(): void {
    clearTimeout(this.focusTimeout);
  }

  agInit(params: ICellEditorParams): void {
    const model: EditableCellModel = params['modelFactory']({
      node: params.node,
      colDef: params.column.getColDef(),
      data: params.node.data,
    });

    const typeOrFactory = params['type'];

    this.required = model.required;
    this.control = createCellFormControl(model);
    this.type = isFunction(typeOrFactory) ? typeOrFactory(params.node.data) : typeOrFactory;
    this.stopEditing = params.stopEditing;
    this.editorOptions = params['options'];

    const { charPress } = params;
    if (charPress) {
      this.isSelectOnFocus = false;
      this.setFirstChar(charPress);
    }
  }

  getValue(): any {
    return this.control.value;
  }

  onBlur(): void {
    this.stopEditing();
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (includes(STOPPED_KEYS, event.keyCode)) {
      event.stopPropagation();
    }
  }

  protected setFirstChar(char: string): void {
    if (this.type) {
      this.control.setValue(isNil(this.type.validateFormat(char)) ? this.type.parse(char) : void 0);
    } else {
      this.control.setValue(char);
    }
  }
}
